import { faArrowLeft } from '@fortawesome/pro-solid-svg-icons';
import Add from '@mui/icons-material/Add';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'src/components/Button';
import { Card } from 'src/components/Card';
import { CircularProgress } from 'src/components/CircularProgress';
import { Dialog } from 'src/components/Dialog';
import { Grid } from 'src/components/Grid';
import { Icon } from 'src/components/Icon';
import { IconButton } from 'src/components/IconButton';
import { SectionHeader } from 'src/components/SectionHeader';
import { Stack } from 'src/components/Stack';
import { TextField, TextFieldProps } from 'src/components/TextField';
import { Tooltip } from 'src/components/Tooltip';
import { Typography } from 'src/components/Typography';
import { externalLinks } from 'src/externalLinks';
import { StripePlan } from 'src/models/payment/_types';
import { fetchStripePlans } from 'src/models/payment/payment';
import {
  SubscriptionPlanForm,
  SubscriptionPlanFormFields,
} from 'src/shared/Forms/SubscriptionPlanForm';
import { Identifier } from 'src/types/utilityTypes';
import { formatCurrency } from 'src/utils/numberFormatter';

import { BaseSelect, BaseSelectOption } from './BaseSelect';

interface ChildrenProps {
  setShowCreatePlanDialog: React.Dispatch<React.SetStateAction<boolean>>;
}

interface PaymentPlanSelectProps extends Omit<TextFieldProps, 'onChange'> {
  userId: Identifier;
  onChange: (value: StripePlan | StripePlan[]) => void;
  excludeDestinationPlans?: boolean;
  multiple?: boolean;
  value?: string | string[];
  onCreatePlan?: (plan: Partial<StripePlan>) => void;
  children?: (props: ChildrenProps) => React.ReactNode;
  packageLinkId?: Identifier;
  hideCreateButton?: boolean;
  /**
   * When provided, enables the one-off payment plan flow.
   * The user will be prompted to choose between creating a reusable
   * payment plan or a one-off plan specific to this subscription.
   * Format: "${itemId}-${userId}-package" (e.g., "123-456-package")
   */
  oneOffSubscriptionId?: string;
}

export const PaymentPlanSelect: React.FC<PaymentPlanSelectProps> = ({
  userId,
  onChange,
  excludeDestinationPlans = false,
  multiple = false,
  value,
  onCreatePlan,
  children,
  packageLinkId,
  hideCreateButton = false,
  oneOffSubscriptionId,
  ...props
}) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'PaymentPlanSelect',
  });
  const [showCreatePlanDialog, setShowCreatePlanDialog] = React.useState(false);
  const [showChooseDialog, setShowChooseDialog] = React.useState(false);
  const [isOneOffPlan, setIsOneOffPlan] = React.useState(false);
  const [formResetKey, setFormResetKey] = React.useState(0);
  // Store one-off plans locally since they won't be returned by the API
  const [localOneOffPlans, setLocalOneOffPlans] = React.useState<StripePlan[]>(
    []
  );

  const curDomain = externalLinks.railsBase || window.location.origin;

  const { data: plans, isLoading } = useQuery({
    queryKey: ['stripePlans', userId],
    queryFn: () => fetchStripePlans(userId),
    staleTime: 60000,
    gcTime: 60000,
  });

  const queryClient = useQueryClient();

  const formatPlanLabel = (plan: StripePlan) => {
    const amount = formatCurrency(plan.amount / 100.0, {
      currency: plan.currency,
    });
    const interval =
      plan.intervalCount > 1
        ? `${plan.intervalCount} ${plan.interval}s`
        : plan.interval;

    return `${plan.name} (${amount}/${interval})`;
  };

  if (isLoading) return <TextField {...props} disabled />;

  const filteredPlans = [
    // Include fetched plans (filtered)
    ...(plans?.filter((plan) => {
      if (!plan.active) return false;
      if (excludeDestinationPlans && plan.metadata.platformId) return false;
      return true;
    }) || []),
    // Include locally stored one-off plans (not returned by API)
    ...localOneOffPlans,
  ];

  // Handler for initiating plan creation - shows choose dialog if oneOffSubscriptionId exists
  const handleInitiateCreatePlan = () => {
    if (oneOffSubscriptionId) {
      setShowChooseDialog(true);
    } else {
      setIsOneOffPlan(false);
      setShowCreatePlanDialog(true);
    }
  };

  // Handler for the choice dialog selection
  const handleChooseOneOff = (oneOff: boolean) => {
    setIsOneOffPlan(oneOff);
    setShowChooseDialog(false);
    setShowCreatePlanDialog(true);
  };

  const handleCreatePlan = async (plan: Partial<StripePlan>) => {
    try {
      await onCreatePlan?.(plan);
      // Only close dialog after successful creation
      setShowCreatePlanDialog(false);

      if (isOneOffPlan && plan.id) {
        // One-off plans won't be returned by the API, so add them to local state
        setLocalOneOffPlans((prev) => [...prev, plan as StripePlan]);
      } else {
        // Regular plans: refetch to get the updated list
        queryClient.invalidateQueries({
          queryKey: ['stripePlans', userId],
        });
      }

      setIsOneOffPlan(false);
      // Reset form by remounting it
      setFormResetKey((prev) => prev + 1);
    } catch (error) {
      // Handle error if needed
    }
  };

  const handleCloseDialog = () => {
    setShowCreatePlanDialog(false);
    setIsOneOffPlan(false);
    // Reset form by remounting it
    setFormResetKey((prev) => prev + 1);
  };

  const handleCloseChooseDialog = () => {
    setShowChooseDialog(false);
  };

  // Build initial plan values for the form, including one-off metadata if applicable
  const getInitialPlanValues = (): Partial<StripePlan> | undefined => {
    if (isOneOffPlan && oneOffSubscriptionId) {
      // Pre-populate required fields for one-off plans since name field is hidden
      // Note: oneOffPaymentPlan can be a string ID (for one-off plans) or boolean in the API
      return {
        name: `One-off Plan ${oneOffSubscriptionId}`,
        metadata: {
          oneOffPaymentPlan: oneOffSubscriptionId,
        },
      } as unknown as Partial<StripePlan>;
    }
    return undefined;
  };

  const chooseDialog = oneOffSubscriptionId && (
    <Dialog
      open={showChooseDialog}
      title={t('createPaymentPlan')}
      onClose={handleCloseChooseDialog}
    >
      <Stack spacing={3}>
        <Button
          variant="bordered"
          fullWidth
          onClick={() => handleChooseOneOff(true)}
          sx={{ py: 2, textAlign: 'left', justifyContent: 'flex-start' }}
        >
          <Stack>
            <Typography variant="subtitle1" fontWeight="bold">
              {t('updateOnlyThisSubscription')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('updateOnlyThisSubscriptionDesc')}
            </Typography>
          </Stack>
        </Button>

        <Button
          variant="bordered"
          fullWidth
          onClick={() => handleChooseOneOff(false)}
          sx={{ py: 2, textAlign: 'left', justifyContent: 'flex-start' }}
        >
          <Stack>
            <Typography variant="subtitle1" fontWeight="bold">
              {t('createReusablePlan')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('createReusablePlanDesc')}
            </Typography>
          </Stack>
        </Button>
      </Stack>
    </Dialog>
  );

  const createPlanDialog = (
    <SubscriptionPlanForm
      key={formResetKey}
      plan={getInitialPlanValues()}
      afterSave={handleCreatePlan}
    >
      {({ submitForm, isValid, isSubmitting }) => (
        <Dialog
          open={showCreatePlanDialog}
          title={
            isOneOffPlan ? t('createOneOffPaymentPlan') : t('createPaymentPlan')
          }
          leftAdornment={
            <IconButton onClick={handleCloseDialog}>
              <Icon icon={faArrowLeft} />
            </IconButton>
          }
          actions={[
            { label: t('cancel'), onClick: handleCloseDialog },
            {
              label: isSubmitting ? t('creating') : t('createPlan'),
              onClick: submitForm,
              disabled: !isValid || isSubmitting,
              variant: 'prominent',
              color: 'primary',
              startIcon: isSubmitting ? (
                <CircularProgress size="small" />
              ) : undefined,
            },
          ]}
        >
          <SubscriptionPlanFormFields isEditing={false} />
        </Dialog>
      )}
    </SubscriptionPlanForm>
  );

  // Convert plans to BaseSelectOption format for both cases
  const baseSelectOptions: BaseSelectOption[] = filteredPlans.map((plan) => ({
    id: plan.id,
    label: formatPlanLabel(plan),
    value: plan,
  }));

  // Create a custom label with the "Create Plan" button
  const baseSelectLabel = props.label && (
    <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      {props.label}
      {onCreatePlan && !hideCreateButton && (
        <Tooltip title={t('createPaymentPlan')}>
          <IconButton onClick={handleInitiateCreatePlan}>
            <Add fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </span>
  );

  const selectedStripePlans = filteredPlans.filter((plan) => {
    if (Array.isArray(value)) {
      return value.includes(plan.id);
    } else {
      return plan.id === value;
    }
  });

  const PackageLinks = packageLinkId && !!selectedStripePlans?.length && (
    <Card sx={{ mt: 2 }}>
      <SectionHeader title={t('paymentOptionLinks')} bottomDivider />

      <Grid container spacing={2} p={2}>
        {/* Default Checkout Link */}
        <Grid size={12}>
          <TextField
            label={t('defaultCheckoutLink')}
            fullWidth
            disabled
            value={`${curDomain}${externalLinks.fbmPackage(packageLinkId)}`}
          />
        </Grid>

        {/* Direct Links for Each Plan */}
        {selectedStripePlans?.map((plan) => (
          <Grid size={12} key={plan.id}>
            <TextField
              label={t('directLinkFor', { plan: formatPlanLabel(plan) })}
              fullWidth
              disabled
              value={`${curDomain}${externalLinks.fbmPackage(
                packageLinkId
              )}?plan_ids=${plan.id}`}
            />
          </Grid>
        ))}
      </Grid>
    </Card>
  );

  if (multiple) {
    return (
      <>
        <BaseSelect
          options={baseSelectOptions}
          value={Array.isArray(value) ? value : value ? [value] : []}
          onChange={(selectedValues) => {
            // BaseSelect with valueOnly=true returns array of ids
            if (Array.isArray(selectedValues)) {
              const selectedPlans = selectedValues
                .map((id) => filteredPlans.find((plan) => plan.id === id))
                .filter(Boolean) as StripePlan[];

              onChange(selectedPlans);
            }
          }}
          label={baseSelectLabel}
          disabled={isLoading || !!props.disabled}
          placeholder={props.placeholder || 'Select plans...'}
          multiple={true}
          valueOnly={true}
        />
        {children?.({ setShowCreatePlanDialog })}
        {PackageLinks}
        {chooseDialog}
        {createPlanDialog}
      </>
    );
  }

  return (
    <>
      <BaseSelect
        options={baseSelectOptions}
        value={value || null}
        onChange={(selectedValue) => {
          if (selectedValue) {
            const selectedPlan = filteredPlans.find(
              (plan) => plan.id === selectedValue
            );
            onChange(selectedPlan as StripePlan);
          } else {
            onChange(null as any);
          }
        }}
        label={baseSelectLabel}
        disabled={isLoading || !!props.disabled}
        placeholder={props.placeholder || 'Select plan...'}
        multiple={false}
        valueOnly={true}
      />
      {children?.({ setShowCreatePlanDialog })}
      {PackageLinks}
      {chooseDialog}
      {createPlanDialog}
    </>
  );
};
